<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link
        :to="{
          name: 'cost-prices-additional-services',
          params: { providerId: $route.params.providerId }
        }"
        class="d-flex align-items-center gap-2"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
        {{ $t('Back to Provider Selection') }}
      </router-link>
    </div>

    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="additionalServicesForm">
        <b-form ref="additionalServicesFormElement" class="" @submit.prevent="submit">
          <b-row>
            <b-col cols="12">
              <div class="additional-services-list">
                <template v-if="loading">
                  <div v-for="i in 3" :key="i" class="service-item">
                    <b-skeleton width="40%" height="24px" class="mb-0" />
                    <b-skeleton width="20%" height="24px" class="mb-0" />
                    <b-skeleton width="20%" height="24px" class="mb-0" />
                  </div>
                </template>
                <template v-else-if="additionalServices.length > 0">
                  <div class="service-item-header">
                    <div class="service-name">
                      <label>{{ t('Service Name') }}</label>
                    </div>
                    <div class="service-price-type">
                      <label>{{ t('Price Type') }}</label>
                    </div>
                    <div class="service-price-value">
                      <label>{{ t('Price Value') }}</label>
                    </div>
                  </div>
                  <div v-for="service in additionalServices" :key="service.id" class="service-item">
                    <!-- Service Name (Readonly) -->
                    <div class="service-name">
                      <label>{{ service.serviceDescription }}</label>
                    </div>

                    <!-- Price Type Selection -->
                    <div class="service-price-type">
                      <label>{{ $t('Fixed') }}</label>
                    </div>

                    <!-- Price Value Input -->
                    <div class="service-price-value">
                      <validation-provider
                        #default="{ errors }"
                        :name="t('Price Value')"
                        :rules="getValidationRules()"
                      >
                        <b-form-input
                          type="number"
                          v-model="service.price"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="empty-state text-center p-4">
                    <feather-icon icon="PackageIcon" size="48" class="mb-2" />
                    <h4>{{ t('No Additional Services') }}</h4>
                    <p class="text-muted">
                      {{ t('There are no additional services available for this provider.') }}
                    </p>
                    <b-button
                      variant="outline-primary"
                      class="mt-2"
                      @click="$router.push({ name: 'cost-prices-additional-services' })"
                    >
                      <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
                      {{ t('Back to List') }}
                    </b-button>
                  </div>
                </template>
              </div>
            </b-col>

            <!-- Submit Button -->
            <b-col cols="12">
              <div
                class="d-flex gap-3 align-items-center justify-content-end mt-2"
                style="gap: 12px"
              >
                <b-button
                  variant="primary"
                  class="d-flex align-items-center"
                  type="submit"
                  :disabled="additionalServices.length === 0"
                >
                  {{ submitting ? t('Please Wait') : t('Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>

<script>
import { BCol, BForm, BRow, BButton, BOverlay, BFormInput, BSkeleton } from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';

export default {
  name: 'CostPriceAdditionalServicesForm',
  components: {
    BOverlay,
    BButton,
    BRow,
    BCol,
    BForm,
    BFormInput,
    ValidationObserver,
    ValidationProvider,
    BSkeleton
  },

  mixins: [helper],

  data() {
    return {
      additionalServices: [],
      priceTypeOptions: [
        { text: 'Sabit (₺)', value: 'fixed' },
        { text: 'Yüzde (%)', value: 'percentage' }
      ],
      submitting: false,
      loading: false
    };
  },

  async created() {
    await this.fetchAdditionalServices();
  },

  methods: {
    async fetchAdditionalServices() {
      try {
        this.loading = true;
        const params = {
          providerId: this.$route.params.providerId
        };
        const response = await useService.providerService.getProviderSpecialServices(
          params.providerId
        );

        this.additionalServices = response.data;
      } catch (error) {
        // console.error('Error fetching additional services:', error);
      } finally {
        this.loading = false;
      }
    },

    handlePriceTypeChange(service, value) {
      this.additionalServices = this.additionalServices.map(s => {
        if (s.id === service.id) {
          s.priceType = value;
          s.priceValue = null;
        }
        return s;
      });
    },

    getValidationRules() {
      const rules = ['required', 'doubleOrInteger', 'positive'];

      return rules.join('|');
    },

    submit() {
      this.$refs.additionalServicesForm.validate().then(success => {
        if (success) {
          const formData = this.additionalServices.map(service => ({
            specialServiceId: service.id,
            price: Number(service.price || 0)
          }));
          this.submitting = true;

          Promise.all(
            formData.map(service =>
              useService.providerService.saveProviderSpecialServices(service.specialServiceId, {
                price: service.price
              })
            )
          )
            .then(() => {
              this.showSuccessMessage(this.$t('Additional services saved successfully'));
              this.$router.push({ name: 'cost-prices-additional-services' });
            })
            .catch(error => {
              this.showErrorMessage(error.response.data.message);
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    }
  },

  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style scoped>
.additional-services-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.service-item {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.service-item-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  border-radius: 4px;
  padding: 0 1rem;
}

.service-name {
  display: flex;
  align-items: center;
}

.service-name label {
  margin: 0;
  font-weight: 500;
}

.service-price-type,
.service-price-value {
  min-width: 200px;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}
</style>
